import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Activity, Plus, Loader2, X } from "lucide-react";
import { ResponsiveContainer, LineChart, Line, YAxis, Tooltip } from "recharts";

const FIELDS = [
  { key: "systolic", label: "Systolic", suffix: "mmHg", short: "SYS", color: "#004AE6" },
  { key: "diastolic", label: "Diastolic", suffix: "mmHg", short: "DIA", color: "#00B3FF" },
  { key: "pulse", label: "Pulse", suffix: "bpm", short: "HR", color: "#EF4444" },
  { key: "temp_f", label: "Temp", suffix: "°F", short: "TEMP", color: "#F59E0B" },
  { key: "o2_sat", label: "O₂", suffix: "%", short: "O2", color: "#FF46A3" },
  { key: "blood_sugar", label: "Sugar", suffix: "mg/dL", short: "BG", color: "#A855F7" },
  { key: "weight_lb", label: "Weight", suffix: "lb", short: "WT", color: "#64748B" },
];

const emptyForm = Object.fromEntries(FIELDS.map((f) => [f.key, ""]));

export default function VitalsPanel({ clientId }) {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/clients/${clientId}/vitals`, { params: { days: 14 } });
      setHistory(data);
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    const payload = {};
    for (const f of FIELDS) {
      const v = form[f.key];
      if (v !== "" && !Number.isNaN(Number(v))) payload[f.key] = Number(v);
    }
    if (Object.keys(payload).length === 0) {
      toast.error("Enter at least one value");
      return;
    }
    setSaving(true);
    try {
      await api.post(`/clients/${clientId}/vitals`, payload);
      toast.success("Vitals logged");
      setForm(emptyForm);
      setOpen(false);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  // sparkline data (reverse-chronological → chronological for chart)
  const sparkData = (key) =>
    history.slice().reverse().map((v) => ({ x: v.recorded_at, y: v[key] }))
      .filter((p) => p.y !== null && p.y !== undefined);

  const latest = history[0];

  return (
    <div className="card-surface p-4" data-testid="vitals-panel">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Activity className="h-4 w-4 text-clinical-primary" />
          <span className="label-eyebrow">Vitals · last 14 days</span>
        </div>
        <button
          onClick={() => setOpen(true)}
          className="inline-flex items-center gap-1.5 rounded-lg bg-clinical-primary text-white text-xs font-medium px-2.5 py-1.5 hover:bg-clinical-primaryHover"
          data-testid="log-vitals-button"
        >
          <Plus className="h-3 w-3" /> Log
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-6 text-sm text-clinical-subtext">
          <Loader2 className="h-4 w-4 animate-spin" />
        </div>
      ) : history.length === 0 ? (
        <div className="text-sm text-clinical-subtext py-4 text-center">
          No vitals logged in the last 14 days.
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {FIELDS.map((f) => {
            const data = sparkData(f.key);
            const last = latest?.[f.key];
            if (last === null || last === undefined) return null;
            return (
              <div
                key={f.key}
                className="rounded-lg border border-clinical-border p-2"
                data-testid={`vital-${f.key}`}
              >
                <div className="flex items-baseline justify-between">
                  <span className="text-[10px] uppercase tracking-wide text-clinical-subtext font-semibold">
                    {f.short}
                  </span>
                  <span className="text-[10px] text-clinical-subtext">{f.suffix}</span>
                </div>
                <div className="font-heading text-xl font-semibold text-clinical-text leading-tight">
                  {last}
                </div>
                {data.length >= 2 && (
                  <div className="h-8 mt-1">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={data}>
                        <YAxis hide domain={["dataMin", "dataMax"]} />
                        <Tooltip
                          contentStyle={{ fontSize: 10, padding: 4, borderRadius: 8 }}
                          labelFormatter={() => ""}
                          formatter={(v) => [`${v} ${f.suffix}`, f.label]}
                        />
                        <Line
                          type="monotone"
                          dataKey="y"
                          stroke={f.color}
                          strokeWidth={1.5}
                          dot={false}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {open && (
        <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/40 p-3" onClick={() => setOpen(false)}>
          <div
            className="bg-white rounded-2xl border border-clinical-border w-full max-w-md max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            data-testid="vitals-modal"
          >
            <div className="sticky top-0 bg-white flex items-center justify-between px-5 py-4 border-b border-clinical-border">
              <h3 className="font-heading text-lg font-medium">Log vitals</h3>
              <button onClick={() => setOpen(false)} className="btn-ghost p-1.5">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="p-5 grid grid-cols-2 gap-3">
              {FIELDS.map((f) => (
                <label key={f.key} className="block">
                  <span className="label-eyebrow mb-1 block">
                    {f.label} <span className="text-clinical-subtext normal-case font-normal">{f.suffix}</span>
                  </span>
                  <input
                    type="number"
                    step="any"
                    value={form[f.key]}
                    onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                    className="input-clinical"
                    data-testid={`vital-input-${f.key}`}
                  />
                </label>
              ))}
            </div>
            <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-clinical-border">
              <button onClick={() => setOpen(false)} className="btn-secondary">Cancel</button>
              <button onClick={save} disabled={saving} className="btn-primary" data-testid="vitals-save">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
